/**
 * The food-fund summary card: collected against target, and the way in.
 *
 * Shared by Home and the Check in screen. Home used to inline it; two copies of
 * a progress bar that has to agree on what "collected" means is one copy too
 * many, especially now that a chip-in confirms itself and the number moves the
 * moment somebody taps.
 */

import { Link } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { useStore } from '../lib/store'
import { Card, Eyebrow, ProgressBar } from './index'

/**
 * The thin version: a rule, a number, a bar.
 *
 * Game day had this and the planning screen didn't, which meant that for six
 * days a week the fund total was only visible if you hadn't yet chipped in —
 * the card above disappears once you have, and with it the only sign of how
 * the week is doing. This strip is the opposite of that card: it asks for
 * nothing, it just says where the money is, so it can sit on both screens
 * whether or not you've paid.
 *
 * Two things the game-day version carried and this one doesn't:
 *
 *   • A "receipts" link. It went to /fund, which no longer has a receipts
 *     section on it, so the label promised something that isn't there. The
 *     whole strip is a link now, which is what people were pressing anyway.
 *   • "$X carried over". Carryover is target-based bookkeeping between weeks
 *     (D46) and it is genuinely confusing next to a total: people read it as
 *     money that is in the pot, or money that isn't, and it is neither.
 *
 * Pending survives, but only when there is any — with chip-ins confirming on
 * arrival it is almost always zero, and "$0 pending" is a line that exists to
 * say nothing.
 */
export function FundStrip({ style }: { style?: CSSProperties }) {
  const { state } = useStore()
  const fund = state?.fund
  if (!fund) return null

  return (
    <section style={style}>
      <Eyebrow>Fund</Eyebrow>
      <Link
        to="/fund"
        className="mono"
        style={{
          display: 'block', padding: '10px 2px', marginTop: 10,
          borderTop: '1px solid var(--line)', color: 'var(--fg)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span>{money(fund.confirmedC)} / {money(fund.targetC)}</span>
          <span style={{ color: 'var(--dim)' }}>
            {fund.targetC > 0 ? `${Math.round((fund.confirmedC / fund.targetC) * 100)}%` : ''}
          </span>
        </div>
        <ProgressBar
          pct={fund.targetC > 0 ? (fund.confirmedC / fund.targetC) * 100 : 0}
          height={4}
          style={{ marginTop: 8, borderRadius: 0 }}
        />
        {fund.pendingC > 0 ? (
          <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 6 }}>
            {money(fund.pendingC)} pending
          </div>
        ) : null}
      </Link>
    </section>
  )
}

export function money(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`
}

export function FundCard({ style }: { style?: CSSProperties }) {
  const { state } = useStore()
  const fund = state?.fund
  if (!fund) return null

  const pct = fund.targetC > 0 ? (fund.confirmedC / fund.targetC) * 100 : 0

  return (
    <Card style={style}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <Eyebrow large>Food fund · {fund.weekLabel}</Eyebrow>
        <span style={{ fontSize: 14, fontWeight: 700, flex: '0 0 auto' }}>
          {money(fund.confirmedC)}{' '}
          <span style={{ color: 'var(--dim)', fontWeight: 500 }}>of {money(fund.targetC)}</span>
        </span>
      </div>

      <ProgressBar pct={pct} style={{ marginTop: 10 }} />

      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 10 }}>
        {fund.iContributed
          ? `You’ve chipped in. Suggested ${money(fund.suggestedC)} each.`
          : `Suggested ${money(fund.suggestedC)}`}
      </div>

      <Link
        to="/fund"
        style={{
          display: 'block', width: '100%', marginTop: 14, padding: 15, minHeight: 48,
          borderRadius: 'var(--radius-btn)', background: 'var(--fill)', color: 'var(--ink)',
          fontSize: 15, fontWeight: 700, textAlign: 'center',
        }}
      >
        {fund.iContributed ? 'Chip in again' : `Chip in ${money(fund.suggestedC)}`}
      </Link>
    </Card>
  )
}
